/**
 * Blogs Page
 * Purpose: Display all blogs with filtering options
 */

import React, { useState, useEffect, useCallback } from 'react';
import { getAllBlogs } from '../services/api';
import BlogCard from '../components/BlogCard';
import Alert from '../components/Alert';

const Blogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [filters, setFilters] = useState({
    category: '',
    author: '',
  });
  const [alert, setAlert] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(true);

  // Fetch blogs on component mount and when filters change
  const fetchBlogs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getAllBlogs(filters);
      console.log('Fetched blogs:', response.data);
      console.log('Number of blogs:', response.data.length);
      console.log('Response structure:', response);

      // Make sure we have an array
      const blogsData = Array.isArray(response.data) ? response.data : [];
      setBlogs(blogsData);

      console.log('Blogs state set to:', blogsData);
    } catch (error) {
      console.error('Error fetching blogs:', error);
      setAlert({
        type: 'error',
        message: error.response?.data?.message || 'Failed to fetch blogs',
      });
      setBlogs([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchBlogs();
  }, [fetchBlogs]);

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value,
    });
  };

  const clearFilters = () => {
    setFilters({ category: '', author: '' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-8 pt-8">
      <div className="container mx-auto px-4">
        <h1 className="text-5xl font-bold text-gray-800 mb-8 animate-fadeIn">
          📚 All Blogs
        </h1>

        <Alert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert({ type: '', message: '' })}
        />

        {/* Filters Section */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8 animate-slideInLeft hover-lift transition-smooth">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            Filter Blogs
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Category Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category
              </label>
              <select
                name="category"
                value={filters.category}
                onChange={handleFilterChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Categories</option>
                <option value="Career">Career</option>
                <option value="Finance">Finance</option>
                <option value="Travel">Travel</option>
              </select>
            </div>

            {/* Author Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Author
              </label>
              <input
                type="text"
                name="author"
                value={filters.author}
                onChange={handleFilterChange}
                placeholder="Search by author name"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Clear Filters Button */}
            <div className="flex items-end">
              <button
                onClick={clearFilters}
                className="w-full bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-xl text-gray-600">Loading blogs...</p>
          </div>
        ) : blogs.length === 0 ? (
          /* No Blogs Found */
          <div className="text-center py-12">
            <p className="text-xl text-gray-600">No blogs found</p>
          </div>
        ) : (
          /* Blogs Grid */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {blogs.map((blog, index) => (
              <BlogCard key={blog._id} blog={blog} index={index} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Blogs;
